import client from 'helpers/client';
import cognito from 'helpers/cognito';

const getAuthHeaders = async () => {
  const auth = await cognito.getHeaderAuthorization();
  return { headers: { ...auth } };
};

const withWallet = (body = {}, walletAddress) => {
  if (!walletAddress) return body || {};
  return { ...(body || {}), walletAddress: String(walletAddress).trim() };
};

const buildQuery = (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, value);
    }
  });
  const query = params.toString();
  return query ? `?${query}` : '';
};

export const getDaoMembers = async (daoId, filters = {}, walletAddress) => {
  const queryFilters = { ...filters };
  if (walletAddress) queryFilters.walletAddress = walletAddress;
  const response = await client.api(
    `/daos/${daoId}/members${buildQuery(queryFilters)}`,
    'get',
    await getAuthHeaders(),
  );
  return response.data;
};

export const updateDaoMemberSettings = async (daoId, allowSelfJoinRequest, walletAddress) => {
  const response = await client.api(
    `/daos/${daoId}/members/settings`,
    'put',
    withWallet({ allowSelfJoinRequest }, walletAddress),
    await getAuthHeaders(),
  );
  return response.data;
};

export const inviteDaoMember = async (daoId, body, walletAddress) => {
  const response = await client.api(
    `/daos/${daoId}/members/invite`,
    'post',
    withWallet(body, walletAddress),
    await getAuthHeaders(),
  );
  return response.data;
};

export const resendDaoMemberInvite = async (daoId, memberId, walletAddress) => {
  const response = await client.api(
    `/daos/${daoId}/members/${memberId}/resend-invite`,
    'post',
    withWallet({}, walletAddress),
    await getAuthHeaders(),
  );
  return response.data;
};

export const cancelDaoMemberInvite = async (daoId, memberId, walletAddress) => {
  const response = await client.api(
    `/daos/${daoId}/members/${memberId}/cancel-invite`,
    'post',
    withWallet({}, walletAddress),
    await getAuthHeaders(),
  );
  return response.data;
};

export const updateDaoMember = async (daoId, memberId, body, walletAddress) => {
  const response = await client.api(
    `/daos/${daoId}/members/${memberId}`,
    'put',
    withWallet(body, walletAddress),
    await getAuthHeaders(),
  );
  return response.data;
};

export const reviewDaoMember = async (daoId, memberId, body, walletAddress) => {
  const response = await client.api(
    `/daos/${daoId}/members/${memberId}/review`,
    'post',
    withWallet(body, walletAddress),
    await getAuthHeaders(),
  );
  return response.data;
};

export const revokeDaoMember = async (daoId, memberId, body = {}, walletAddress) => {
  const response = await client.api(
    `/daos/${daoId}/members/${memberId}/revoke`,
    'post',
    withWallet(body, walletAddress),
    await getAuthHeaders(),
  );
  return response.data;
};

export const reapproveDaoMember = async (daoId, memberId, walletAddress) => {
  const response = await client.api(
    `/daos/${daoId}/members/${memberId}/reapprove`,
    'post',
    withWallet({}, walletAddress),
    await getAuthHeaders(),
  );
  return response.data;
};

export const reinviteDaoMember = async (daoId, memberId, walletAddress) => {
  const response = await client.api(
    `/daos/${daoId}/members/${memberId}/reinvite`,
    'post',
    withWallet({}, walletAddress),
    await getAuthHeaders(),
  );
  return response.data;
};

export const getDaoMemberInvitation = async (token) => {
  const response = await client.api(`/dao-members/invitations/${token}`, 'get', await getAuthHeaders());
  return response.data;
};

export const acceptDaoMemberInvitation = async (token, walletAddress) => {
  const response = await client.api(
    `/dao-members/invitations/${token}/accept`,
    'post',
    withWallet({}, walletAddress),
    await getAuthHeaders(),
  );
  return response.data;
};

export const declineDaoMemberInvitation = async (token) => {
  const response = await client.api(`/dao-members/invitations/${token}/decline`, 'post', {}, await getAuthHeaders());
  return response.data;
};

export const getMyDaoMembership = async (daoId) => {
  const response = await client.api(`/daos/${daoId}/members/me`, 'get', await getAuthHeaders());
  return response.data;
};

export const selfRequestDaoMembership = async (daoId, body = {}, walletAddress) => {
  const response = await client.api(
    `/daos/${daoId}/members/self-request`,
    'post',
    withWallet(body, walletAddress),
    await getAuthHeaders(),
  );
  return response.data;
};

export const withdrawMyDaoMembership = async (daoId) => {
  const response = await client.api(`/daos/${daoId}/members/me/withdraw`, 'post', {}, await getAuthHeaders());
  return response.data;
};

export default {
  getDaoMembers,
  updateDaoMemberSettings,
  inviteDaoMember,
  resendDaoMemberInvite,
  cancelDaoMemberInvite,
  updateDaoMember,
  reviewDaoMember,
  revokeDaoMember,
  reapproveDaoMember,
  reinviteDaoMember,
  getDaoMemberInvitation,
  acceptDaoMemberInvitation,
  declineDaoMemberInvitation,
  getMyDaoMembership,
  selfRequestDaoMembership,
  withdrawMyDaoMembership,
};
